import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../integrations/supabase/client';
import { useAuth } from './useAuth';

export interface AppSettings {
  [key: string]: any;
}

export const useUserSettings = <TSettings extends AppSettings = AppSettings>(appId: string, defaultSettings: TSettings = {} as TSettings) => {
  const { user } = useAuth();

  const sb: any = supabase;
  const localKey = `MapleOS_user_settings:${appId}`;

  const readLocal = (): TSettings | null => {
    try {
      const raw = localStorage.getItem(localKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? (parsed as TSettings) : null;
    } catch {
      return null;
    }
  };

  const writeLocal = (value: TSettings) => {
    try {
      localStorage.setItem(localKey, JSON.stringify(value));
    } catch {
      // ignore storage errors
    }
  };

  const clearLocal = () => {
    try {
      localStorage.removeItem(localKey);
    } catch {
      // ignore storage errors
    }
  };

  // Seed from localStorage first to avoid UI flashes and allow persistence even if Supabase is slow.
  const seed = () => ({ ...defaultSettings, ...(readLocal() ?? {}) }) as TSettings;

  const [settings, setSettings] = useState<TSettings>(seed);
  const settingsRef = useRef<TSettings>(seed());
  const [isLoaded, setIsLoaded] = useState(false);

  // Keep an always-fresh reference to the latest settings to avoid stale-closure overwrites
  useEffect(() => {
    settingsRef.current = settings;
  }, [settings]);

  // Load settings on mount and when user changes
  useEffect(() => {
    const loadSettings = async () => {
      const local = readLocal();
      const mergedLocal = { ...defaultSettings, ...(local ?? {}) } as TSettings;

      // Always apply local immediately so the UI is stable.
      setSettings(mergedLocal);
      settingsRef.current = mergedLocal;

      if (!user) {
        setIsLoaded(true);
        return;
      }

      try {
        const { data: settingsData } = await sb
          .from('os_user_settings')
          .select('settings')
          .eq('user_id', user.id)
          .eq('app_id', appId)
          .maybeSingle();

        if (settingsData && settingsData.settings) {
          const merged = { ...defaultSettings, ...(settingsData.settings as TSettings) } as TSettings;
          setSettings(merged);
          settingsRef.current = merged;
          writeLocal(merged);
        } else {
          // If Supabase has no row yet but local has values, keep local and backfill Supabase.
          if (local && Object.keys(local).length > 0) {
            try {
              await sb
                .from('os_user_settings')
                .upsert(
                  { user_id: user.id, app_id: appId, settings: mergedLocal },
                  { onConflict: 'user_id,app_id' }
                );
            } catch {
              // ignore
            }
          } else {
            setSettings(defaultSettings);
            settingsRef.current = defaultSettings;
            writeLocal(defaultSettings);
          }
        }
      } catch (error) {
        console.error(`Failed to load settings for ${appId}:`, error);
        // keep local state as fallback
      } finally {
        setIsLoaded(true);
      }
    };

    loadSettings();
  }, [appId, user]);

  // Save settings
  const saveSettings = useCallback(
    async (newSettings: Partial<TSettings>) => {
      const prevSettings = settingsRef.current;
      const updatedSettings = { ...prevSettings, ...newSettings } as TSettings;

      // Update state immediately so subsequent updates merge correctly even before a re-render
      settingsRef.current = updatedSettings;
      setSettings(updatedSettings);

      // Always mirror to localStorage so reopening windows doesn't reset.
      writeLocal(updatedSettings);

      if (!user) return;

      try {
        const { error } = await sb
          .from('os_user_settings')
          .upsert(
            {
              user_id: user.id,
              app_id: appId,
              settings: updatedSettings
            },
            { onConflict: 'user_id,app_id' }
          );

        if (error) throw error;

        console.log(`Settings saved for ${appId}:`, updatedSettings);
      } catch (error) {
        console.error(`Failed to save settings for ${appId}:`, error);
        // Keep local + in-memory settings; they will be re-synced on next load.
        settingsRef.current = updatedSettings;
        setSettings(updatedSettings);
        writeLocal(updatedSettings);
      }
    },
    [appId, user]
  );

  // Update a single setting
  const updateSetting = useCallback(
    <K extends keyof TSettings>(key: K, value: TSettings[K]) => {
      saveSettings({ [key]: value } as unknown as Partial<TSettings>);

    },
    [saveSettings]
  );

  // Reset to defaults
  const resetSettings = useCallback(async () => {
    setSettings(defaultSettings);
    settingsRef.current = defaultSettings;
    clearLocal();

    if (!user) return;

    try {
      const { error } = await sb
        .from('os_user_settings')
        .upsert(
          {
            user_id: user.id,
            app_id: appId,
            settings: defaultSettings
          },
          {
            onConflict: 'user_id,app_id'
          }
        );

      if (error) throw error;

      console.log(`Settings reset for ${appId}`);
    } catch (error) {
      console.error(`Failed to reset settings for ${appId}:`, error);
    }
  }, [appId, user, defaultSettings]);

  return {
    settings,
    isLoaded,
    saveSettings,
    updateSetting,
    resetSettings
  };
};

// User desktop configuration hook
export const useUserDesktopConfig = () => {
  const { user } = useAuth();
  const [desktopConfig, setDesktopConfig] = useState<any>({});
  const [isLoaded, setIsLoaded] = useState(false);

  const sb: any = supabase;

  useEffect(() => {
    const loadConfig = async () => {
      if (!user) {
        setDesktopConfig({});
        setIsLoaded(true);
        return;
      }

      try {
        const { data: configData } = await sb
          .from('os_user_desktop')
          .select('config_key, config_value, updated_at')
          .eq('user_id', user.id)
          .order('updated_at', { ascending: false });

        if (configData) {
          // Deduplicate per key by taking the newest (thanks to order by updated_at desc)
          const config = configData.reduce((acc: any, item: any) => {
            if (acc[item.config_key] === undefined) {
              acc[item.config_key] = item.config_value;
            }
            return acc;
          }, {});
          setDesktopConfig(config);
        }
      } catch (error) {
        console.error('Failed to load desktop config:', error);
      } finally {
        setIsLoaded(true);
      }
    };

    loadConfig();
  }, [user]);

  const saveDesktopConfig = useCallback(async (key: string, config: any) => {
    if (!user) {
      console.warn('Cannot save desktop config: user not authenticated');
      return;
    }

    try {
      setDesktopConfig(prev => ({ ...prev, [key]: config }));

      const { error } = await sb
        .from('os_user_desktop')
        .upsert(
          {
            user_id: user.id,
            config_key: key,
            config_value: config,
            updated_at: new Date().toISOString()
          },
          { onConflict: 'user_id,config_key' } // <-- ensures real UPSERT not duplicate inserts
        );

      if (error) throw error;

      console.log('Desktop config saved:', key, config);
    } catch (error) {
      console.error('Failed to save desktop config:', error);
    }
  }, [user]);

  return {
    desktopConfig,
    isLoaded,
    saveDesktopConfig
  };
};